// SPDX-License-Identifier: Elastic-2.0
/**
 * HeroCard — top-of-dashboard status hero.
 *
 * Mirrors the design-system reference (`ui_kits/dashboard/B1Home.jsx`)
 * one-to-one: a single big card with an embedded peach-lavender orb
 * (positioned upper-right), a time-aware greeting, the "Tu casa está
 * tranquila" headline + suggestion-count sub, a status pill in the
 * top-right corner, three at-a-glance metrics, and a row of scene
 * chips at the bottom.
 *
 * The orb lives INSIDE the card here — not as a full-page background
 * (which is what the first dashboard-orbs pass shipped). The
 * embedded variant is brighter (opacity 0.58 dark / 0.78 light,
 * single 70px-blur layer) than the dashboard-wide `GradientOrb
 * variant="subtle"` it replaces. Kept inline (no GradientOrb
 * component reuse) because the dimensions + opacity are hero-card-
 * specific — sharing wires would force a third variant on
 * GradientOrb that nobody else needs.
 *
 * Data:
 *  - Greeting name from `useAuth(state => state.user?.display_name)`
 *    with a friendly fallback when local-only / no profile loaded.
 *  - Time-of-day eyebrow from `Date#getHours()` — 6-12 buenos días,
 *    12-19 buenas tardes, else buenas noches. (i18n string per
 *    bucket, not interpolated time-of-day text — locales differ.)
 *  - Suggestion count from `usePendingSuggestionsCount()` so the
 *    sub-headline stays in sync with whatever the bell badge shows.
 *  - Device count from `useDevices()` — only counts `available:true`
 *    so an offline bulb doesn't pad the number.
 *  - Temp + Consumo are static placeholders (see TODO) — F-300 has
 *    no `/api/mirlai/v1/home/summary` endpoint yet; once it ships
 *    those wire through here.
 *
 * Scene chips are local-state-only for now: clicking sets the active
 * chip but doesn't dispatch a scene. The real wiring lands once
 * F-300 surfaces a `POST /api/mirlai/v1/scenes/:id/activate`
 * endpoint (tracked as a follow-up).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon, type IconName } from "../../components/Icon/Icon";
import { useAuth } from "../../api/auth-store";

import { useDevices } from "./useDevices";
import { usePendingSuggestionsCount } from "../suggestions/useSuggestions";

import styles from "./HeroCard.module.css";

interface SceneOption {
  id: string;
  labelKey: string;
  icon: IconName;
}

/** Fixed scene set — order mirrors the design's B1Home SCENES. */
const SCENES: readonly SceneOption[] = [
  { id: "morning", labelKey: "dashboard.hero.scenes.morning", icon: "sun" },
  { id: "work", labelKey: "dashboard.hero.scenes.work", icon: "music" },
  { id: "dinner", labelKey: "dashboard.hero.scenes.dinner", icon: "bulb" },
  { id: "night", labelKey: "dashboard.hero.scenes.night", icon: "moon" },
  { id: "away", labelKey: "dashboard.hero.scenes.away", icon: "shield" },
];

/** Pick the right time-of-day greeting bucket. */
function greetingKey(): string {
  const h = new Date().getHours();
  if (h >= 6 && h < 12) return "dashboard.hero.greeting.morning";
  if (h >= 12 && h < 19) return "dashboard.hero.greeting.afternoon";
  return "dashboard.hero.greeting.night";
}

export function HeroCard(): JSX.Element {
  const { t } = useTranslation();
  const [activeScene, setActiveScene] = useState<string>("work");

  const userName = useAuth((s) => s.user?.display_name ?? null);
  const suggestionCount = usePendingSuggestionsCount();
  const { data: devicesData } = useDevices();
  const activeDeviceCount = devicesData?.devices.filter((d) => d.available !== false).length ?? 0;

  const greeting = t(greetingKey(), {
    name: userName ?? t("dashboard.hero.greeting.fallback_name"),
  });

  return (
    <section className={styles.card} data-testid="hero-card" aria-label={t("dashboard.title")}>
      {/* No embedded orb here anymore — the page-level GradientOrb
       *  rendered by DashboardLayout provides the ambient atmosphere
       *  for the whole main column, matching the landing page hero
       *  composition. The card sits on top with a semi-translucent
       *  surface so the orb peeks through the edges. */}
      <div className={styles.body}>
        <div className={styles.lead}>
          <div className={styles.eyebrow}>{greeting}</div>
          <h1 className={styles.headline}>
            {t("dashboard.hero.headline.prefix")}
            {" "}
            <em className={styles.headlineEm}>
              {t("dashboard.hero.headline.adjective")}
            </em>
            .
          </h1>
          <p className={styles.sub}>
            {t("dashboard.hero.sub", { count: suggestionCount })}
          </p>

          <div className={styles.metrics}>
            <Metric label={t("dashboard.hero.metrics.temp_label")} value="—" />
            <Metric label={t("dashboard.hero.metrics.energy_label")} value="—" />
            <Metric
              label={t("dashboard.hero.metrics.devices_label")}
              value={t("dashboard.hero.metrics.devices_value", {
                count: activeDeviceCount,
              })}
            />
          </div>
        </div>

        <div className={styles.statusBlock}>
          <span
            className={styles.statusPill}
            data-testid="hero-status-pill"
            aria-label={t("dashboard.hero.status.all_connected")}
          >
            <span className={styles.statusDot} />
            {t("dashboard.hero.status.all_connected")}
          </span>
          <p className={styles.statusMeta}>
            {t("dashboard.hero.status.last_update", { seconds: 12 })}
            <br />
            <span className={styles.statusMetaDim}>
              {t("dashboard.hero.status.local_mode")}
            </span>
          </p>
        </div>
      </div>

      <div
        className={styles.scenes}
        role="radiogroup"
        aria-label={t("dashboard.hero.scenes.label")}
      >
        {SCENES.map((scene) => {
          const active = scene.id === activeScene;
          return (
            <button
              key={scene.id}
              type="button"
              role="radio"
              aria-checked={active}
              className={`${styles.sceneChip} ${active ? styles.sceneChipActive : ""}`}
              onClick={() => setActiveScene(scene.id)}
              data-testid={`hero-scene-${scene.id}`}
            >
              <Icon name={scene.icon} size={15} />
              {t(scene.labelKey)}
            </button>
          );
        })}
      </div>
    </section>
  );
}

interface MetricProps {
  label: string;
  value: string;
}

function Metric({ label, value }: MetricProps): JSX.Element {
  return (
    <div className={styles.metric}>
      <div className={styles.metricLabel}>{label}</div>
      <div className={styles.metricValue}>{value}</div>
    </div>
  );
}
